import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { User, GraduationCap, Globe, Heart, Calendar } from 'lucide-react';
import aboutImage from '../images/about.png';
import SilkCSS from "../components/SilkCSS";
import ProfileCard from "./ProfileCard";

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const personalInfo = [
    { icon: Calendar, label: 'Âge', value: '22 ans' },
    { icon: Globe, label: 'Localisation', value: 'Casablanca, Maroc' },
    { icon: GraduationCap, label: 'Formation', value: 'Génie Logiciel' },
    { icon: Heart, label: 'Centres d’intérêt', value: 'Kickboxing, Football, Voyage' },
  ];

  const languages = [
    { name: 'Français'},
    { name: 'Anglais'},
    { name: 'Arabe'},
  ];

  return (
    <section id="about" className="section-padding bg-gray-50 dark:bg-dark-900 relative overflow-hidden">
      {/* Silk background */}
      <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
        <SilkCSS speed={5} scale={1} color="#7B7481" noiseIntensity={1.5} rotation={0} />
      </div>
      <div className="container-custom relative z-10">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            À <span className="gradient-text">propos</span>
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Apprenez à mieux me connaître : mon parcours, mes compétences et ce qui me motive dans le monde de la tech.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Colonne gauche - Photo & infos */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="space-y-8"
          >
            {/* Profile Card */}
            <div className="flex justify-center">
              <ProfileCard
                name="Ez-zouek Hamza"
                title="Développeur Full Stack"
                handle="hamzazawak"
                status="Online"
                contactText="Me contacter"
                avatarUrl={aboutImage}
                showUserInfo={true}
                enableTilt={true}
                enableMobileTilt={false}
                onContactClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              />
            </div>

            {/* Informations personnelles */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Informations personnelles
              </h3>
              <div className="space-y-4">
                {personalInfo.map((info, index) => (
                  <motion.div
                    key={info.label}
                    initial={{ opacity: 0, x: -20 }}
                    animate={inView ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="flex items-center space-x-4"
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                      <info.icon size={20} className="text-primary-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{info.label}</p>
                      <p className="font-medium text-gray-900 dark:text-white">{info.value}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Colonne droite - Bio & langues */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="space-y-8"
          >
            {/* Biographie */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Biographie
              </h3>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p>
                  Étudiant passionné par la programmation et spécialisé en génie logiciel, je me consacre au développement web, aussi bien en backend qu’en frontend. Mon parcours a débuté par une grande curiosité pour la création de solutions numériques qui font la différence.
                </p>
                <p>
                  En tant que développeur full-stack, j’aime intervenir sur toute la chaîne technique, de la conception d’interfaces à la réalisation de backends robustes. Mon approche allie expertise technique et créativité, avec le souci constant de livrer des solutions de qualité et évolutives.
                </p>
                <p>
                  Je crois en l’apprentissage continu et veille à rester à jour sur les dernières technologies et bonnes pratiques. Mon objectif : contribuer à des projets innovants qui me poussent à évoluer professionnellement et personnellement.
                </p>
              </div>
            </div>

            {/* Langues */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Langues
              </h3>
              <div className="space-y-4">
                {languages.map((language, index) => (
                  <motion.div
                    key={language.name}
                    initial={{ opacity: 0, y: 20 }}
                    animate={inView ? { opacity: 1, y: 0 } : {}}
                    transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                    className="flex items-center justify-between"
                  >
                    <span className="font-medium text-gray-900 dark:text-white">
                      {language.name}
                    </span>
                    <span className="px-3 py-1 bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200 rounded-full text-sm font-medium">
                      {language.level}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* Qualités */}
            <div className="bg-white dark:bg-dark-800 rounded-2xl p-6 shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
                Qualités principales
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  'Curieux & Innovant',
                  'Autonome',
                  'Apprenant en continu',
                  'Esprit de résolution de problèmes',
                  'Travail en équipe',
                  'Sens du détail'
                ].map((quality, index) => (
                  <motion.div
                    key={quality}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={inView ? { opacity: 1, scale: 1 } : {}}
                    transition={{ duration: 0.5, delay: 0.6 + index * 0.1 }}
                    className="flex items-center space-x-2"
                  >
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span className="text-gray-700 dark:text-gray-300">{quality}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About; 